import React, { memo, useEffect } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import cn from "classnames";
import { noop } from "@litres/utils";

import pubsub from "pubsub";
import { Head } from "./components/Head";
import styles from "./MobileModal.module.scss";
import type { IButtonProps } from "../../button";
import { Button } from "../../button";

export interface MobileModalProps {
  visible: boolean;
  head: React.ReactNode;
  content: React.ReactNode;
  className?: string;
  contentClassName?: string;
  onBackClick?: () => void;
  footerButton?: Omit<IButtonProps, "mode" | "size" | "fullWidth">;
  footerContent?: React.ReactNode;
  backArrowOff?: boolean;
}

export const MobileModal = memo(
  ({
    visible,
    head,
    content,
    className,
    contentClassName,
    onBackClick = noop,
    footerButton,
    footerContent,
    backArrowOff = false,
  }: MobileModalProps) => {
    useEffect(() => {
      if (!visible) {
        pubsub.publish("helpdeskeddy-show-widget");
        return;
      }

      pubsub.publish("helpdeskeddy-hide-widget");
      return () => {
        pubsub.publish("helpdeskeddy-show-widget");
      };
    }, [visible]);

    if (!visible) {
      return null;
    }

    return createPortal(
      <RemoveScroll>
        <div
          className={cn(styles.mobileModal, className)}
          data-testid="mobileModal"
        >
          <Head
            head={head}
            backArrowVisible={!backArrowOff}
            onBackClick={onBackClick}
          />
          <div
            className={cn(styles.mobileModal__content, contentClassName)}
            data-testid="mobileModal__content"
          >
            {content}
          </div>
          {(footerButton || footerContent) && (
            <div className={styles.footer}>
              {footerButton && (
                <Button
                  mode="secondary"
                  size="medium"
                  fullWidth
                  {...footerButton}
                />
              )}
              {footerContent}
            </div>
          )}
        </div>
      </RemoveScroll>,
      document.body
    );
  }
);

MobileModal.displayName = "MobileModal";
